import { EditOutlined, PlayCircleOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Modal,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Upload,
} from 'antd';
import React, { useMemo } from 'react';
import type {
  Dashboard,
  FacturiScadenteScheduleStatus,
  LatestImportStatus,
  NotificationPreflightStatus,
  NotificationRunStatus,
  ValidationError,
} from '../../DTOS/FacturiScadente';

interface DashboardTabProps {
  dashboardData?: Dashboard;
  latestImportData?: LatestImportStatus;
  notificationStatusData?: NotificationRunStatus;
  preflightData?: NotificationPreflightStatus;
  scheduleData?: FacturiScadenteScheduleStatus;
  validationErrors: ValidationError[];
  isDashboardLoading: boolean;
  isLatestImportLoading: boolean;
  isNotificationStatusLoading: boolean;
  isPreflightLoading: boolean;
  isScheduleLoading: boolean;
  canManage: boolean;
  invoiceUploadProps: UploadProps;
  companyUploadProps: UploadProps;
  isImportingInvoices: boolean;
  isImportingCompanies: boolean;
  isApproving: boolean;
  isRunning: boolean;
  onApprove: () => void;
  onRunNow: () => void;
  onRefresh: () => void;
  onEditFromError: (error: ValidationError) => void;
}

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-';
  }
  return new Date(value).toLocaleString('ro-RO');
};

const formatReason = (reason?: string) => {
  switch (reason) {
    case 'missing email':
      return 'Lipseste email';
    case 'invalid email':
      return 'Email invalid';
    case 'missing client':
      return 'Client lipsa';
    default:
      return reason || '-';
  }
};

const formatRunType = (runType?: string) => {
  switch (runType) {
    case 'scheduled':
      return 'Programata';
    case 'manual_client':
      return 'Manuala (client)';
    case 'manual_all':
      return 'Manuala (toate)';
    default:
      return '-';
  }
};

const formatDayOfWeek = (value?: string) => {
  switch ((value || '').toLowerCase()) {
    case 'monday':
      return 'Luni';
    case 'tuesday':
      return 'Marti';
    case 'wednesday':
      return 'Miercuri';
    case 'thursday':
      return 'Joi';
    case 'friday':
      return 'Vineri';
    case 'saturday':
      return 'Sambata';
    case 'sunday':
      return 'Duminica';
    default:
      return value || '-';
  }
};

const DashboardTab: React.FC<DashboardTabProps> = ({
  dashboardData,
  latestImportData,
  notificationStatusData,
  preflightData,
  scheduleData,
  validationErrors,
  isDashboardLoading,
  isLatestImportLoading,
  isNotificationStatusLoading,
  isPreflightLoading,
  isScheduleLoading,
  canManage,
  invoiceUploadProps,
  companyUploadProps,
  isImportingInvoices,
  isImportingCompanies,
  isApproving,
  isRunning,
  onApprove,
  onRunNow,
  onRefresh,
  onEditFromError,
}) => {
  const importStatusTag =
    latestImportData?.status === 'VALID' ? (
      <Tag color='green'>VALID</Tag>
    ) : latestImportData?.status === 'INVALID' ? (
      <Tag color='red'>INVALID</Tag>
    ) : (
      <Tag>Fara import</Tag>
    );

  const runStatusTag =
    notificationStatusData?.status === 'SENT' ? (
      <Tag color='green'>Trimise</Tag>
    ) : notificationStatusData?.status === 'BLOCKED' ? (
      <Tag color='red'>Blocate</Tag>
    ) : notificationStatusData?.status === 'ERROR' ? (
      <Tag color='red'>Eroare</Tag>
    ) : (
      <Tag>-</Tag>
    );

  const approvedTag = latestImportData?.isApproved ? (
    <Tag color='green'>Aprobat</Tag>
  ) : (
    <Tag color='orange'>Neaprobat</Tag>
  );

  const errorColumns = useMemo(
    () => [
      {
        title: 'Client',
        dataIndex: 'clientName',
        key: 'clientName',
        render: (value: string, record: ValidationError) => value || record.clientCode || '-',
      },
      {
        title: 'Cod',
        dataIndex: 'clientCode',
        key: 'clientCode',
        render: (value: string) => value || '-',
      },
      {
        title: 'Motiv',
        dataIndex: 'reason',
        key: 'reason',
        render: (value: string) => formatReason(value),
      },
      {
        title: 'Actiuni',
        key: 'actions',
        render: (_: unknown, record: ValidationError) => (
          <Button
            size='small'
            icon={<EditOutlined />}
            onClick={() => onEditFromError(record)}
            disabled={!record.clientId && !record.clientCode && !record.clientName}
          >
            Editeaza
          </Button>
        ),
      },
    ],
    [onEditFromError],
  );

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={6}>
          <Card>
            <Statistic title='Clienti restanti' value={dashboardData?.overdueClients ?? 0} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic title='Fara email' value={dashboardData?.missingEmailClients ?? 0} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic title='Emailuri esuate' value={dashboardData?.failedEmails ?? 0} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic title='Necesita mapare' value={dashboardData?.needsMappingInvoices ?? 0} />
          </Card>
        </Col>
      </Row>

      <Divider />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={10}>
          <Card title='Importuri'>
            <Space direction='vertical' size='middle' style={{ width: '100%' }}>
              <Upload {...invoiceUploadProps}>
                <Button icon={<UploadOutlined />} loading={isImportingInvoices} style={{ width: '100%' }}>
                  Incarca facturi scadente
                </Button>
              </Upload>
              <Upload {...companyUploadProps}>
                <Button icon={<UploadOutlined />} loading={isImportingCompanies} style={{ width: '100%' }}>
                  Incarca clienti SAGA
                </Button>
              </Upload>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card
            title='Status import & notificari'
            extra={
              <Space>
                {canManage && (
                  <Button
                    onClick={onApprove}
                    loading={isApproving}
                    disabled={!latestImportData?.importBatchId || latestImportData?.isApproved}
                  >
                    Aproba date
                  </Button>
                )}
                {canManage && (
                  <Button
                    icon={<PlayCircleOutlined />}
                    onClick={() => {
                      Modal.confirm({
                        title: 'Confirmare trimitere notificari',
                        content: (
                          <div>
                            <div>Clienti restanti: {preflightData?.overdueClients ?? 0}</div>
                            <div>Pregatiti: {preflightData?.readyToSendClients ?? 0}</div>
                            <div>Email lipsa: {preflightData?.missingEmailClients ?? 0}</div>
                            <div>Email invalid: {preflightData?.invalidEmailClients ?? 0}</div>
                            <div>Categorii dezactivate: {preflightData?.categoryDisabledClients ?? 0}</div>
                            <div>Notificari oprite: {preflightData?.notificationsOffClients ?? 0}</div>
                            <div>Facturi dezactivate: {preflightData?.invoiceNotificationsOffClients ?? 0}</div>
                            <div>Clienti lipsa: {preflightData?.missingClientMappings ?? 0}</div>
                            {preflightData?.sampleRecipients?.length ? (
                              <div style={{ marginTop: 8 }}>
                                Exemple destinatari: {preflightData.sampleRecipients.join(', ')}
                              </div>
                            ) : null}
                          </div>
                        ),
                        okText: 'Trimite',
                        cancelText: 'Renunta',
                        onOk: onRunNow,
                      });
                    }}
                    loading={isRunning}
                    disabled={!latestImportData?.isApproved || preflightData?.isBlocked}
                  >
                    Ruleaza acum
                  </Button>
                )}
                <Button icon={<ReloadOutlined />} onClick={onRefresh}>
                  Reincarca
                </Button>
              </Space>
            }
          >
            {(isLatestImportLoading || isNotificationStatusLoading || isDashboardLoading || isScheduleLoading) && (
              <Spin size='small' style={{ marginBottom: 16 }} />
            )}
            <Descriptions size='small' column={2}>
              <Descriptions.Item label='Status import'>{importStatusTag}</Descriptions.Item>
              <Descriptions.Item label='Aprobare'>{approvedTag}</Descriptions.Item>
              <Descriptions.Item label='Fisier'>{latestImportData?.fileName || '-'}</Descriptions.Item>
              <Descriptions.Item label='Incarcat la'>{formatDateTime(latestImportData?.uploadedAt)}</Descriptions.Item>
              <Descriptions.Item label='Incarcat de'>{latestImportData?.uploadedBy || '-'}</Descriptions.Item>
              <Descriptions.Item label='Aprobat la'>{formatDateTime(latestImportData?.approvedAt)}</Descriptions.Item>
              <Descriptions.Item label='Aprobat de'>{latestImportData?.approvedBy || '-'}</Descriptions.Item>
              <Descriptions.Item label='Randuri'>{latestImportData?.rowCount ?? 0}</Descriptions.Item>
              <Descriptions.Item label='Ultima rulare'>
                {formatDateTime(notificationStatusData?.runAt)}
              </Descriptions.Item>
              <Descriptions.Item label='Urmatoarea trimitere'>
                {formatDateTime(scheduleData?.nextRunAt)}
              </Descriptions.Item>
              <Descriptions.Item label='Programare saptamanala'>
                {`${formatDayOfWeek(scheduleData?.dayOfWeek)} ${String(scheduleData?.hour ?? 0).padStart(2, '0')}:${String(
                  scheduleData?.minutes ?? 0,
                ).padStart(2, '0')}`}
              </Descriptions.Item>
              <Descriptions.Item label='Status rulare'>{runStatusTag}</Descriptions.Item>
              <Descriptions.Item label='Trimise'>{notificationStatusData?.sentCount ?? 0}</Descriptions.Item>
              <Descriptions.Item label='Esuate'>{notificationStatusData?.failedCount ?? 0}</Descriptions.Item>
              <Descriptions.Item label='Tip rulare'>{formatRunType(notificationStatusData?.runType)}</Descriptions.Item>
            </Descriptions>
            {latestImportData?.status === 'VALID' && latestImportData?.isApproved && (
              <Alert
                type='success'
                showIcon
                style={{ marginTop: 12 }}
                message={`Notificarile sunt programate saptamanal (${formatDayOfWeek(scheduleData?.dayOfWeek)}).`}
              />
            )}
            {latestImportData?.status === 'INVALID' && (
              <Alert
                type='warning'
                showIcon
                style={{ marginTop: 12 }}
                message='Exista clienti cu email lipsa/invalid'
                description='Notificarile vor fi trimise doar clientilor eligibili.'
              />
            )}
            {latestImportData?.status === 'VALID' && !latestImportData?.isApproved && (
              <Alert
                type='warning'
                showIcon
                style={{ marginTop: 12 }}
                message='Notificarile sunt oprite pana la aprobare.'
              />
            )}
            <Divider />
            <Card size='small' title='Verificare inainte de trimitere'>
              {isPreflightLoading ? (
                <Spin size='small' />
              ) : (
                <Descriptions size='small' column={2}>
                  <Descriptions.Item label='Clienti restanti'>{preflightData?.overdueClients ?? 0}</Descriptions.Item>
                  <Descriptions.Item label='Pregatiti'>{preflightData?.readyToSendClients ?? 0}</Descriptions.Item>
                  <Descriptions.Item label='Email lipsa'>{preflightData?.missingEmailClients ?? 0}</Descriptions.Item>
                  <Descriptions.Item label='Email invalid'>{preflightData?.invalidEmailClients ?? 0}</Descriptions.Item>
                  <Descriptions.Item label='Categorii dezactivate'>
                    {preflightData?.categoryDisabledClients ?? 0}
                  </Descriptions.Item>
                  <Descriptions.Item label='Notificari oprite'>
                    {preflightData?.notificationsOffClients ?? 0}
                  </Descriptions.Item>
                  <Descriptions.Item label='Facturi dezactivate'>
                    {preflightData?.invoiceNotificationsOffClients ?? 0}
                  </Descriptions.Item>
                  <Descriptions.Item label='Clienti lipsa'>
                    {preflightData?.missingClientMappings ?? 0}
                  </Descriptions.Item>
                </Descriptions>
              )}
            </Card>
          </Card>
        </Col>
      </Row>

      <Divider />

      <Card title='Erori validare import'>
        {validationErrors.length === 0 ? (
          <Alert type='success' showIcon message='Nu exista erori de validare.' />
        ) : (
          <Table
            rowKey={(record) => record.clientId || record.clientCode || record.clientName || ''}
            columns={errorColumns}
            dataSource={validationErrors}
            pagination={false}
          />
        )}
      </Card>
    </>
  );
};

export default DashboardTab;
